import { ChangeEvent, FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';
import cleanText from 'utils/cleanText';

import ComposerField from 'modules/ai/components/ComposerField';
import {
  AiEmbedContext,
  EMBED_AI_SUGGESTIONS,
} from 'modules/ai/constants/embedAiSuggestions';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import { useEmbedAiComposer } from 'modules/ai/hooks/useEmbedAiComposer';
import { useEmbedAiIntro } from 'modules/ai/hooks/useEmbedAiIntro';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';
import { NaviOrbVisual } from 'modules/ai/pages/SocietyAiAgent/components/NaviOrb';
import ThinkingDots from 'modules/ai/pages/SocietyAiAgent/components/ThinkingDots';

import './EmbedFloatingComposer.scss';

type EmbedFloatingComposerProps = {
  context?: AiEmbedContext;
  societyId: string;
  societyName?: string;
  maxSuggestions?: number;
};

const EmbedFloatingComposer: FC<EmbedFloatingComposerProps> = ({
  context = 'society',
  societyId,
  societyName = '',
  maxSuggestions = 4,
}) => {
  const { t } = useTranslate();
  const mixpanel = useMixpanel();
  const hasTrackedMountRef = useRef(false);
  const hasTrackedFocusRef = useRef(false);

  const isAdmin = useSelector((state: State) => state.society?.role?.isAdmin);

  const hasAiAccess = useSelector(
    (state: State) =>
      state.society?.actualSociety?.configuration?.aiPlan?.enabled === true &&
      isAdmin === true
  );

  const {
    inputText,
    isStreaming,
    selectSuggestion,
    handleSubmit,
    setInputText,
  } = useEmbedAiComposer({ societyId, embedContext: context });
  const { rootClassName, handleIntroEnd, shouldAnimate } = useEmbedAiIntro();

  const suggestionKeys = (
    EMBED_AI_SUGGESTIONS[context] ?? EMBED_AI_SUGGESTIONS.society
  ).slice(0, maxSuggestions);

  useEffect(() => {
    if (hasTrackedMountRef.current || !hasAiAccess) return;
    hasTrackedMountRef.current = true;

    trackAiActivity({
      mixpanel,
      state: store.getState() as State,
      eventName: AI_EVENTS.EMBED_VIEWED,
      extras: {
        surface: 'embed',
        embedContext: context,
        suggestionCount: suggestionKeys.length,
        introAnimationShown: !shouldAnimate,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasAiAccess) {
    return null;
  }

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(cleanText(event.target.value));
  };

  const handleFocus = () => {
    if (hasTrackedFocusRef.current) return;
    hasTrackedFocusRef.current = true;
    trackAiActivity({
      mixpanel,
      state: store.getState() as State,
      eventName: AI_EVENTS.COMPOSER_FOCUSED,
      extras: {
        surface: 'embed',
        embedContext: context,
      },
    });
  };

  return (
    <section
      className={rootClassName}
      data-testid="embed-floating-composer"
      aria-label={t('CHATBOT_Embed_Label')}
    >
      <div className="navi-embed__card" onAnimationEnd={handleIntroEnd}>
        <div className="navi-embed__top-row">
          <div className="navi-embed__brand">
            <NaviOrbVisual size={32} showSparkles isThinking={isStreaming} />
            <span
              className="navi-embed__title-slot"
              role={isStreaming ? 'status' : undefined}
              aria-live={isStreaming ? 'polite' : undefined}
              aria-label={isStreaming ? t('CHATBOT_Thinking') : undefined}
            >
              {isStreaming ? (
                <ThinkingDots />
              ) : (
                <span className="navi-embed__title">
                  {t('CHATBOT_Embed_Title')}
                </span>
              )}
            </span>
          </div>

          <ComposerField
            variant="embed"
            value={inputText}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onFocus={handleFocus}
            disabled={isStreaming}
            placeholder={t('CHATBOT_Embed_Placeholder', {
              societyName: societyName || t('Society'),
            })}
            sendAriaLabel={t('CHATBOT_Embed_Send')}
            inputTestId="embed-composer-input"
            sendTestId="embed-composer-send"
          />
        </div>

        {suggestionKeys.length > 0 && (
          <div
            className="navi-embed__suggestions right-aligned"
            role="list"
            aria-label={t('CHATBOT_Embed_SuggestionsLabel')}
          >
            {suggestionKeys.map((key) => (
              <button
                key={key}
                type="button"
                className="navi-embed__chip"
                // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
                role="listitem"
                disabled={isStreaming}
                onClick={() => selectSuggestion(t(key), key)}
                data-testid={`embed-suggestion-${key}`}
              >
                {t(key)}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default EmbedFloatingComposer;
